import { useQuery } from "@tanstack/react-query";
import { addMinutes, compareAsc, format, parseISO } from "date-fns";
import { ResourceType } from "@gettactic/api";
import { api } from "@/lib/api/api";
import { useIsDesignSystem2 } from "@gettactic/hooks";

type UseResKey = {
  officeId: string;
  durationMins: number;
  // Attention! This may not be week days actually, but multi-date picker's dates.
  weekDatesStr: string[];
  resourceType: ResourceType;
  startTime: Date;
  filterParent?: string | null;
  organizer?: string | null;
};

// We do pass weekDatesStr, which is multiple days,
// but we only get data for a single day (first one).
export default function useReserveResources({
  officeId,
  durationMins,
  weekDatesStr,
  resourceType,
  startTime,
  filterParent,
  organizer = null
}: UseResKey) {
  const isV2 = useIsDesignSystem2();

  // single date component used for meeting rooms only
  const isSingleDate = resourceType === "meeting_room";

  function getEntities(entities: ResourceType) {
    const types: ResourceType[] =
      entities === "desk" || entities === "workspace"
        ? ["desk", "workspace"]
        : entities === "parking_space" || entities === "parking_lot"
          ? ["parking_lot", "parking_space"]
          : ["meeting_room"];
    return types;
  }

  const { data: office } = useQuery(
    ["organizations.offices", officeId],
    async () => {
      const res = await api.client.organizations.office(officeId!);
      return res.result;
    },
    {
      enabled: !!officeId
    }
  );

  const { data: resourcesList } = useQuery(
    [
      "organizations.schedulable",
      officeId,
      weekDatesStr,
      resourceType,
      filterParent,
      startTime,
      durationMins,
      organizer
    ],
    async () => {
      const startFormatted = format(startTime, "HH:mm:ss");
      const endFormatted = format(
        addMinutes(startTime, durationMins),
        "HH:mm:ss"
      );
      const endDateFormatted = format(
        addMinutes(startTime, durationMins),
        "yyyy-MM-dd"
      );
      const dates = weekDatesStr.map((w) => {
        return `${w}T${startFormatted}.000Z`;
      });

      const endDate = weekDatesStr.map((w) => {
        return `${w}T${endFormatted}.000Z`;
      });
      if (isV2) {
        const { startDateAndTime, endDateAndTime } = getStartEndDate(
          weekDatesStr,
          startTime,
          durationMins,
          isSingleDate
        );

        const res = await api.client.organizations.getSchedulableResources({
          officeId: officeId!,
          types: getEntities(resourceType),
          parentId: filterParent,
          startDate: startDateAndTime,
          endDate: endDateAndTime,
          duration: durationMins.toString(),
          hierarchy: !!filterParent,
          manage: false,
          organizerId: organizer
        });
        return res.result?.available || [];
      }

      const res = await api.client.organizations.schedulable(
        officeId!,
        getEntities(resourceType),
        filterParent,
        dates,
        durationMins.toString(),
        null,
        undefined,
        !!filterParent,
        false,
        organizer
      );
      return res.result;
    },
    { enabled: !!officeId }
  );

  const { data: parkingResourcesList } = useQuery(
    [
      "organizations.schedulable",
      officeId,
      weekDatesStr,
      getEntities("parking_space"),
      startTime,
      durationMins,
      organizer
    ],
    async () => {
      const startFormatted = format(startTime, "HH:mm:ss");
      const dates = weekDatesStr.map((w) => {
        return `${w}T${startFormatted}.000Z`;
      });

      if (isV2) {
        const { startDateAndTime, endDateAndTime } = getStartEndDate(
          weekDatesStr,
          startTime,
          durationMins,
          isSingleDate
        );
        const res = await api.client.organizations.getSchedulableResources({
          officeId: officeId!,
          types: getEntities("parking_space"),
          startDate: startDateAndTime,
          endDate: endDateAndTime,
          duration: durationMins.toString(),
          hierarchy: !!filterParent,
          manage: false,
          organizerId: organizer
        });
        return res.result?.available || [];
      }

      const res = await api.client.organizations.schedulable(
        officeId!,
        getEntities("parking_space"),
        null,
        dates,
        durationMins.toString(),
        null,
        undefined,
        undefined,
        false,
        organizer
      );
      return res.result;
    },
    {
      enabled:
        !!officeId &&
        !!office &&
        office.total_parking_spaces + office.total_parking_lots > 0
    }
  );

  return {
    resourcesList,
    office,
    parkingResourcesList
  };
}

/**
 * @param weekDatesStr - Attention! This are not week days actually,
 *                       but multi-date datepicker's dates.
 *                       It should be used to get the earliest date.
 * @param startTime - The start time
 * @param durationMins - The duration in minutes
 * @param isSingleDate - Whether the resource is a meeting room, it's important
 *                        because is the only reliable way to determine where we get date info from,
 *                        because in case of meeting room, we get data for a single day (startDateAndTime),
 *                        but in case of other resources, we get data for multiple days (weekDatesStr).
 * @returns The start date and time and the end date and time
 */
const getStartEndDate = (
  weekDatesStr: string[],
  startTime: Date,
  durationMins: number,
  isSingleDate: boolean
) => {
  if (!isSingleDate) {
    const earliestDate = Array.from(weekDatesStr).sort((a, b) =>
      compareAsc(parseISO(a), parseISO(b))
    )[0];
    const startTimeFormatted = format(startTime, "HH:mm:ss");

    const endTimeFormatted = format(
      addMinutes(startTime, durationMins),
      "HH:mm:ss"
    );

    // user can schedule late in the day, so reservation can end on the next day
    const earliestDateWithTime = parseISO(
      `${earliestDate}T${startTimeFormatted}`
    );
    const endDate = format(
      addMinutes(earliestDateWithTime, durationMins),
      "yyyy-MM-dd"
    );
    const answer = {
      startDateAndTime: `${earliestDate}T${startTimeFormatted}.000Z`,
      endDateAndTime: `${endDate}T${endTimeFormatted}.000Z`
    };
    return answer;
  }
  const startTimeFormatted = format(startTime, "HH:mm:ss");
  const endTimeFormatted = format(
    addMinutes(startTime, durationMins),
    "HH:mm:ss"
  );

  // user can schedule late in the day, so reservation can end on the next day
  const endDate = format(addMinutes(startTime, durationMins), "yyyy-MM-dd");
  return {
    startDateAndTime: `${weekDatesStr[0]}T${startTimeFormatted}.000Z`,
    endDateAndTime: `${endDate}T${endTimeFormatted}.000Z`
  };
};
